import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { getErrorMessage } from '@/lib/api'
import { useAssignWidgetTemplate, useCreateWidgetTemplate } from '@/hooks/api/useWidgets'
import { useCustomers } from '@/hooks/api/useCustomers'
import { usePermissions } from '@/hooks/api/usePermissions'
import { WidgetTemplateNameModal } from './components/WidgetTemplateNameModal'
import { createDefaultStyleFormState, toTemplateStyle } from './components/widgetConfig.serializers'

/**
 * A template is named before it exists: naming it is the only step of creation,
 * and everything else is edited on the saved template.
 */
export function WidgetTemplateCreatePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const base = 'resources.widgets'
  const { isAdmin, isPartner } = usePermissions()
  const canSelectCustomer = isAdmin || isPartner
  const { data: customers = [] } = useCustomers(canSelectCustomer)

  const [searchParams] = useSearchParams()
  // Set when the picker on the assistants tab starts a template for one venue.
  const assignTo = Number(searchParams.get('assign')) || null
  const assignedCustomerId = Number(searchParams.get('customer')) || undefined

  const createTemplate = useCreateWidgetTemplate()
  const assignTemplate = useAssignWidgetTemplate()

  const handleConfirm = async (name: string, selectedCustomerId?: number) => {
    const customerId = assignedCustomerId ?? selectedCustomerId
    try {
      const template = await createTemplate.mutateAsync({
        name,
        kind: 'single',
        style: toTemplateStyle(createDefaultStyleFormState()),
        ...(canSelectCustomer && customerId ? { customer_id: customerId } : {}),
      })

      if (assignTo) {
        await assignTemplate.mutateAsync({ assistant_id: assignTo, template_id: template.id })
      }

      toast.success(t(`${base}.editor.created_toast`))
      navigate(`/widgets/templates/${template.id}`, { replace: true })
    } catch (error) {
      toast.error(t(`${base}.editor.save_failed_toast`, { error: getErrorMessage(error) }))
    }
  }

  return (
    <WidgetTemplateNameModal
      isOpen
      initialName=""
      customerOptions={
        canSelectCustomer && !assignedCustomerId
          ? customers.map((customer) => ({ value: customer.id.toString(), label: customer.name }))
          : undefined
      }
      initialCustomerId={assignedCustomerId}
      isCustomerRequired={canSelectCustomer && !assignedCustomerId}
      isSaving={createTemplate.isPending || assignTemplate.isPending}
      onClose={() => navigate('/widgets?tab=templates')}
      onConfirm={(name, customerId) => void handleConfirm(name, customerId)}
    />
  )
}

export default WidgetTemplateCreatePage
